import { XMarkIcon } from '@heroicons/react/24/outline';
import { PropsWithChildren } from 'react';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { exportFormatOptions } from '@/sections/export/constants';
import type { DropdownOption } from '@/types/dropdown.types';
import { pluralize } from '@/utils/pluralize.util';

type ExportModalProps = PropsWithChildren & {
  open: boolean;
  onClose: () => void;
  type: string;
  selected?: {
    selectedRows: string[];
    onClearSelectedRows: () => void;
  };
  onExport: () => void;
  exportFormat: DropdownOption;
  onChangeExportFormat: (option: DropdownOption) => void;
  disabled?: boolean;
  loading?: boolean;
  formatOptions?: DropdownOption[];
};

export function ExportModal({
  open,
  onClose,
  type,
  children,
  selected,
  onExport,
  exportFormat,
  onChangeExportFormat,
  disabled,
  loading,
  formatOptions,
}: ExportModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      hideClose
      baseClassName="overflow-visible"
      footer={{
        text: 'Export',
        disabled,
        onClick: onExport,
        // left: (
        //   <Button
        //     className="border-0 p-0"
        //     label="Set as Default Setting"
        //     styleType="tertiary"
        //     onClick={onClose}
        //   />
        // ),
      }}
      title={<h2 className="py-2 text-lg font-bold">{type} Export Options</h2>}
      className="scrollbar-dark border-chariot-stroke overflow-visible border-2 border-x-0 p-0"
    >
      {selected && selected.selectedRows.length > 0 && (
        <div className="border-chariot-stroke flex items-center justify-between border-b-2 px-6 py-4">
          <p>
            Export limited to {selected.selectedRows.length} selected{' '}
            {pluralize(selected.selectedRows.length, type).toLocaleLowerCase()}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={selected.onClearSelectedRows}
          >
            <XMarkIcon className="size-4" />
            Clear
          </Button>
        </div>
      )}

      <div className="scrollbar-dark max-h-[550px] overflow-auto px-3 py-4">
        {loading && (
          <>
            <Loader isLoading={true} className="mb-4 h-[200px] w-full" />
            <Loader isLoading={true} className="mb-4 h-[200px] w-full" />
            <Loader isLoading={true} className="h-[200px] w-full" />
          </>
        )}
        {!loading && <>{children}</>}
      </div>

      <div
        data-testid="export-format"
        className="border-chariot-stroke relative z-10 flex items-center justify-between border-t-2 px-6 py-4"
      >
        <p>Export Format</p>
        <Select
          value={(exportFormat?.value as string | undefined) ?? ''}
          onValueChange={value => {
            const options = formatOptions ?? exportFormatOptions;
            const option = options.find(o => o.value === value);
            if (option) {
              onChangeExportFormat(option);
            }
          }}
        >
          <SelectTrigger className="w-fit">
            <SelectValue placeholder="Select format" />
          </SelectTrigger>
          <SelectContent>
            {(formatOptions ?? exportFormatOptions).map(option => (
              <SelectItem key={option.value} value={option.value as string}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Modal>
  );
}
